/* eslint-disable react-refresh/only-export-components -- Context files export context, hooks, and provider together by design */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type React from "react";
import { ThemeMode, THEME_MODE_VALUES } from "@shared/types/theme-mode.types";
import type { ThemeModeType } from "@shared/types/theme-mode.types";

function getQueryParamThemeOverride(): ThemeModeType | undefined {
  const raw = new URLSearchParams(window.location.search).get("theme");
  return THEME_MODE_VALUES.find((v) => v === raw);
}

const APP_THEME_PROVIDER_NOT_FOUND_ERROR =
  "useAppThemeContext must be used within an <AppThemeProvider> component. " +
  "Ensure the component is wrapped in <AppThemeProvider>.";

class AppThemeProviderNotFoundError extends Error {
  constructor() {
    super(APP_THEME_PROVIDER_NOT_FOUND_ERROR);
    this.name = "AppThemeProviderNotFoundError";
  }
}

interface AppThemeContextValue {
  readonly themeMode: ThemeModeType | undefined;
  readonly setThemeMode: (mode: ThemeModeType | undefined) => void;
}

const AppThemeContext = createContext<AppThemeContextValue | null>(null);

export function useAppThemeContext(): AppThemeContextValue {
  const ctx = useContext(AppThemeContext);
  if (ctx === null) {
    throw new AppThemeProviderNotFoundError();
  }
  return ctx;
}

interface AppThemeProviderProps {
  readonly children: React.ReactNode;
}

export function AppThemeProvider({
  children,
}: AppThemeProviderProps): React.JSX.Element {
  const [queryParamOverride] = useState(getQueryParamThemeOverride);
  const [themeMode, rawSetThemeMode] = useState<ThemeModeType | undefined>(
    queryParamOverride,
  );

  const setThemeMode = useCallback(
    (mode: ThemeModeType | undefined): void => {
      if (queryParamOverride === undefined) {
        rawSetThemeMode(mode);
      }
    },
    [queryParamOverride],
  );

  useEffect(() => {
    const html = document.documentElement;

    if (themeMode === ThemeMode.DARK) {
      html.classList.add("dark");
    } else if (themeMode === ThemeMode.LIGHT) {
      html.classList.remove("dark");
    } else {
      const prefersDark = window.matchMedia(
        "(prefers-color-scheme: dark)",
      ).matches;
      html.classList.toggle("dark", prefersDark);
    }
  }, [themeMode]);

  const value = useMemo(
    (): AppThemeContextValue => ({ themeMode, setThemeMode }),
    [themeMode, setThemeMode],
  );

  return (
    <AppThemeContext.Provider value={value}>
      {children}
    </AppThemeContext.Provider>
  );
}
